import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { Wordmark } from "./Logo";

const go = (e, href) => {
  if (href.startsWith("/#")) {
    e.preventDefault();
    if (window.location.pathname === "/") {
      document.querySelector(href.slice(1))?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = href;
    }
  }
};

const resultsLinks = [
  { label: "Results", href: "/#results", testid: "nav-results-link" },
  { label: "Case Studies", href: "/case-studies", testid: "nav-case-studies-link" },
];

const aboutLinks = [
  { label: "About Us", href: "/about", testid: "nav-about-link" },
  { label: "Our Process", href: "/process", testid: "nav-process-link" },
  { label: "Contact Us", href: "/contact", testid: "nav-contact-link" },
];

const NavDropdown = ({ label, items, baseTestId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        data-testid={baseTestId}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors"
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18 }}
            className="absolute top-full left-0 mt-3 min-w-[180px] rounded-xl border border-slate-200 bg-white shadow-[0_20px_50px_-15px_rgba(15,23,42,0.2)] py-2 z-50"
          >
            {items.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={l.testid}
                onClick={(e) => {
                  go(e, l.href);
                  setOpen(false);
                }}
                className="block px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-[#0FA89E] hover:bg-slate-50 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const mobileGroups = [
    { label: "Services", href: "/services", testid: "nav-link-services" },
    ...resultsLinks,
    ...aboutLinks,
  ];

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_8px_30px_-15px_rgba(15,23,42,0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="nav-logo-mark" className="shrink-0">
            <Wordmark />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/services"
              data-testid="nav-link-services"
              className="link-underline text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors"
            >
              Services
            </Link>
            <NavDropdown label="Results" items={resultsLinks} baseTestId="nav-link-results" />
            <NavDropdown label="About" items={aboutLinks} baseTestId="nav-link-about" />
          </nav>

          <div className="hidden md:block">
            <Link
              to="/contact"
              data-testid="nav-cta-contact-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0FA89E] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0C8E85]"
            >
              Book a consultation
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-slate-700"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200 overflow-hidden"
          >
            <div className="px-4 py-6 flex flex-col gap-5">
              {mobileGroups.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={(e) => {
                    go(e, l.href);
                    setOpen(false);
                  }}
                  className="text-base font-medium text-slate-700"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-contact-button"
                onClick={() => setOpen(false)}
                className="inline-flex w-fit items-center gap-2 rounded-full bg-[#0FA89E] px-5 py-2.5 text-sm font-semibold text-white"
              >
                Book a consultation
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
